import React from "react";
import PolicyPage from "./PolicyPage";

const TOC = [
  { id: "principles",  label: "Principles" },
  { id: "hardlines",   label: "Hard lines" },
  { id: "mature",      label: "Mature Realms" },
  { id: "moderation",  label: "Moderation" },
  { id: "reporting",   label: "Reporting" },
];

export default function ContentPolicy() {
  return (
    <PolicyPage
      testId="page-content-policy"
      path="/content-policy"
      title="Content Policy | TalesTalesTales"
      description="What is and isn't allowed in TalesTalesTales sagas, community worlds, and Mature Realms. Hard lines, moderation, and how to report abuse."
      eyebrow="Content Policy"
      h1="Content policy"
      toc={TOC}
    >
      <p className="italic text-amber-100/60">
        TalesTalesTales is a storytelling platform. This policy sets out what is and isn&rsquo;t
        allowed inside sagas, in community-published worlds, and in the opt-in Mature Realms
        add-on.
      </p>

      <section id="principles">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Principles</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Standard sagas are written to be safe for a general adult audience by default.</li>
          <li>Mature Realms is opt-in, 18+, age-verified, and gated behind a paid tier.</li>
          <li>We do not moderate individual private sagas by default. Community-published worlds are moderated.</li>
        </ul>
      </section>

      <section id="hardlines">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Hard lines (never allowed, any mode)</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Sexual content involving minors.</strong> No CSAM, no age-play, no fictional characters described as minors in sexual scenarios.</li>
          <li><strong>Real people in sexual scenarios.</strong> No identifiable real living people depicted sexually, whether public figures or private individuals.</li>
          <li><strong>Non-consent as titillation.</strong> Depiction of non-consensual sexual acts framed as arousing is blocked.</li>
          <li><strong>Bestiality, incest, and other content prohibited by the underlying model providers.</strong></li>
          <li><strong>Content that facilitates real-world harm</strong> — instructions for weapons of mass destruction, bio/chemical weapons synthesis, credible threats against identifiable persons.</li>
          <li><strong>Doxxing or harassment</strong> of other users or staff.</li>
        </ul>
      </section>

      <section id="mature">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Mature Realms (opt-in, 18+)</h2>
        <p>Inside Mature Realms, consenting adult scenarios between fictional adult characters are permitted, subject to the hard lines above. Content categories the add-on may enable:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Explicit consensual sexual scenarios between adult characters.</li>
          <li>Uncensored language and adult themes appropriate to genre fiction (crime, war, horror).</li>
          <li>Graphic violence appropriate to the setting.</li>
        </ul>
        <p className="text-amber-100/60 text-sm">
          Access is limited to verified adults on an active paid tier. Mature Realms never activates
          automatically on a standard saga.
        </p>
      </section>

      <section id="moderation">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Moderation</h2>
        <p>
          Automated safety systems from the underlying model providers block hard-line requests
          regardless of mode. Community-published worlds are reviewed before they appear in the
          public library. We reserve the right to remove content that violates this policy and to
          suspend accounts responsible for repeat violations.
        </p>
      </section>

      <section id="reporting">
        <h2 className="font-display text-2xl text-amber-50 mt-6 mb-3">Reporting</h2>
        <p>
          If you find a community world or saga that violates this policy, report it via
          <a href="mailto:abuse@talestalestales.com" className="underline hover:text-amber-100"> abuse@talestalestales.com</a>{" "}
          or through the <a href="/support" className="underline hover:text-amber-100">support page</a>.
          We aim to review reports within 48 hours.
        </p>
      </section>
    </PolicyPage>
  );
}
